<template>
  <Modal
    :show="true"
    headerVariant="blue"
    size="md"
    dialogMaxWidth="600px"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    :zIndex="10100"
    @close="closeModal"
  >
    <template #header>
      <div class="template-header__text">
        <h2 class="template-header__title">Edit Template</h2>
        <p class="template-header__subtitle">Update template details</p>
      </div>
      <button type="button" class="template-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>

    <template #body>
      <Form id="edit-template-form" class="form" @submit="editTemplate">
        <div class="form-body">
          <div class="form-grid">
            <div class="form-field">
              <Input
                v-model="editTemplateObj.name"
                label="Template Name"
                type="text"
                placeholder="Enter template name"
                size="sm"
                required
                :error="errors.name"
              />
            </div>
            <div class="form-field">
              <Input
                v-model="editTemplateObj.shortcut"
                label="Shortcut"
                type="text"
                placeholder="Enter shortcut"
                size="sm"
                required
                :error="errors.shortcut"
              />
            </div>
            <div class="form-field form-field-full">
              <Input
                v-model="editTemplateObj.subject"
                label="Subject Name"
                type="text"
                placeholder="Enter email subject"
                size="sm"
                required
                :error="errors.subject"
              />
            </div>
            <div class="form-field form-field-full">
              <div class="content-label-row">
                <label for="content" class="content-label">
                  Add Note<span class="required-field">*</span>
                </label>
                <TemplateVariablePicker @insert="insertVariable" />
              </div>
              <RichTextEditor
                ref="contentEditor"
                v-model="editTemplateObj.content"
                height="150px"
                placeholder="Enter your template content here..."
                accepts-uploads
                @upload="handleEditorUpload"
              />
            </div>

            <div class="form-field form-field-full">
              <label for="track-performance" class="content-label"> Track Performance </label>
              <div class="template-stats-section">
                <div class="template-stats-grid">
                  <div class="stat-card">
                    <Icon icon="mdi:content-copy" class="stat-icon" width="16" height="16" />
                    <div class="stat-value">{{ editTemplateObj.copyCount || 0 }}</div>
                    <div class="stat-label">Times Copied</div>
                  </div>
                  <div class="stat-card">
                    <Icon icon="mdi:thumb-up" class="stat-icon positive" width="16" height="16" />
                    <div class="stat-controls-inline">
                      <button
                        @click="adjustStat('positiveReplies', -1)"
                        :disabled="isPending || isMarkingPositive || isMarkingNegative"
                      >
                        −
                      </button>
                      <div class="stat-value">
                        {{ editTemplateObj.positiveReplies || 0 }}
                      </div>
                      <button
                        @click="adjustStat('positiveReplies', 1)"
                        :disabled="isPending || isMarkingPositive || isMarkingNegative"
                      >
                        +
                      </button>
                    </div>
                    <div class="stat-label">Positive</div>
                  </div>
                  <div class="stat-card">
                    <Icon icon="mdi:thumb-down" class="stat-icon negative" width="16" height="16" />
                    <div class="stat-controls-inline">
                      <button
                        @click="adjustStat('negativeReplies', -1)"
                        :disabled="isPending || isMarkingPositive || isMarkingNegative"
                      >
                        −
                      </button>
                      <div class="stat-value">
                        {{ editTemplateObj.negativeReplies || 0 }}
                      </div>
                      <button
                        @click="adjustStat('negativeReplies', 1)"
                        :disabled="isPending || isMarkingPositive || isMarkingNegative"
                      >
                        +
                      </button>
                    </div>
                    <div class="stat-label">Negative</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </template>

    <template #footer>
      <Button
        type="button"
        variant="secondary"
        size="md"
        @click="closeModal"
        :disabled="isPending || isMarkingPositive || isMarkingNegative"
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="edit-template-form"
        variant="primary"
        size="md"
        :loading="isPending"
        :disabled="isMarkingPositive || isMarkingNegative"
      >
        Save
      </Button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Form } from "vee-validate";
import { Button, Icon, Input, Modal, RichTextEditor } from "@/components/ui";
import TemplateVariablePicker from "@/components/template/TemplateVariablePicker.vue";
import Template from "@/services/template/template";
import {
  useUpdateTemplate,
  useMarkTemplatePositive,
  useMarkTemplateNegative,
} from "@/composables/templates";
// Props
const props = defineProps({
  selectedTemplate: {
    type: Object,
    default: null,
  },
});

// Get global properties
const $toast = useToast();

// Store
const store = useStore();
const organizationId = computed(() => store.getters.account.data?.organizationId);

// Emits
const emit = defineEmits(["closeEditTemplateModal", "refreshTemplates"]);

// Form state
const editTemplateObj = ref({
  id: "",
  name: "",
  shortcut: "",
  subject: "",
  content: "",
  lastUsedAt: new Date(),
  templateFolderId: "",
  organizationId: "",
  positiveReplies: 0,
  negativeReplies: 0,
  copyCount: 0,
});

// Form errors
const errors = ref({});

// Content editor ref, used to insert a picked variable token at the cursor
const contentEditor = ref(null);

const insertVariable = (token) => {
  contentEditor.value?.insertTextAtCursor(token);
};

const handleEditorUpload = async (payload) => {
  const { file, onProgress, onSuccess, onError, signal } = payload;
  try {
    const response = await Template.uploadAttachment(file, { signal, onProgress });
    const url = response.data?.data?.url;
    if (url) {
      onSuccess?.(url);
    } else {
      onError?.(response.data?.msg || "Upload failed");
    }
  } catch (error) {
    if (
      error?.code === "ERR_CANCELED" ||
      error?.name === "AbortError" ||
      error?.name === "CanceledError"
    ) {
      return;
    }
    onError?.(error?.response?.data?.msg || error?.message || "Upload failed");
  }
};

// TanStack Query mutations
const { mutateAsync: updateTemplate, isPending } = useUpdateTemplate();
const { mutateAsync: markPositive, isPending: isMarkingPositive } = useMarkTemplatePositive();
const { mutateAsync: markNegative, isPending: isMarkingNegative } = useMarkTemplateNegative();

// Methods
const closeModal = () => {
  emit("closeEditTemplateModal");
};

const setValues = () => {
  if (!props.selectedTemplate) {
    console.warn("No selected template provided to edit modal");
    return;
  }

  editTemplateObj.value.id = props.selectedTemplate.id;
  editTemplateObj.value.name = props.selectedTemplate.name;
  editTemplateObj.value.shortcut = props.selectedTemplate.shortcut;
  editTemplateObj.value.subject = props.selectedTemplate.subject;
  editTemplateObj.value.negativeReplies = props.selectedTemplate.negativeReplies || 0;
  editTemplateObj.value.positiveReplies = props.selectedTemplate.positiveReplies || 0;
  editTemplateObj.value.copyCount = props.selectedTemplate.copied || 0;
  editTemplateObj.value.templateFolderId = props.selectedTemplate.templateFolderId || "";
  editTemplateObj.value.organizationId =
    props.selectedTemplate.organizationId || organizationId.value;
  editTemplateObj.value.content = props.selectedTemplate.content || "";
};

const validateForm = () => {
  errors.value = {};

  if (!editTemplateObj.value.name || editTemplateObj.value.name.trim() === "") {
    errors.value.name = "Template Name is required";
  }

  if (!editTemplateObj.value.shortcut || editTemplateObj.value.shortcut.trim() === "") {
    errors.value.shortcut = "Shortcut is required";
  }

  if (!editTemplateObj.value.subject || editTemplateObj.value.subject.trim() === "") {
    errors.value.subject = "Subject Name is required";
  }

  if (!editTemplateObj.value.content || editTemplateObj.value.content.trim() === "") {
    errors.value.content = "Content is required";
  }

  return Object.keys(errors.value).length === 0;
};

const editTemplate = async () => {
  if (!validateForm()) {
    return;
  }

  try {
    await updateTemplate({
      id: editTemplateObj.value.id,
      name: editTemplateObj.value.name,
      shortcut: editTemplateObj.value.shortcut,
      subject: editTemplateObj.value.subject,
      content: editTemplateObj.value.content,
      lastUsedAt: new Date(),
      templateFolderId: editTemplateObj.value.templateFolderId,
      organizationId: editTemplateObj.value.organizationId,
    });

    $toast?.success("Template updated successfully!");
    emit("refreshTemplates");
    closeModal();
  } catch (error) {
    console.error("Error updating template:", error);
    $toast?.error(error.message || "Failed to update template. Please try again.");
  }
};

const positiveReply = async (operation) => {
  if (!props.selectedTemplate?.id) return;

  try {
    await markPositive({
      id: props.selectedTemplate.id,
      operation,
    });
    if (operation === "inc") {
      $toast?.success("Template marked as positive!");
      editTemplateObj.value.positiveReplies = (editTemplateObj.value.positiveReplies || 0) + 1;
    } else {
      editTemplateObj.value.positiveReplies = Math.max(
        0,
        (editTemplateObj.value.positiveReplies || 0) - 1
      );
    }
    emit("refreshTemplates");
  } catch (error) {
    console.error("Error marking template as positive:", error);
    $toast?.error(error.message || "Failed to mark template as positive.");
  }
};

const negativeReply = async (operation) => {
  if (!props.selectedTemplate?.id) return;

  try {
    await markNegative({
      id: props.selectedTemplate.id,
      operation,
    });
    if (operation === "inc") {
      $toast?.success("Template marked as negative!");
      editTemplateObj.value.negativeReplies = (editTemplateObj.value.negativeReplies || 0) + 1;
    } else {
      editTemplateObj.value.negativeReplies = Math.max(
        0,
        (editTemplateObj.value.negativeReplies || 0) - 1
      );
    }
    emit("refreshTemplates");
  } catch (error) {
    console.error("Error marking template as negative:", error);
    $toast?.error(error.message || "Failed to mark template as negative.");
  }
};

const adjustStat = async (field, delta) => {
  if (field === "positiveReplies") {
    await positiveReply(delta > 0 ? "inc" : "dec");
  } else if (field === "negativeReplies") {
    await negativeReply(delta > 0 ? "inc" : "dec");
  }
};

// Watch for selectedTemplate changes
watch(
  () => props.selectedTemplate,
  () => {
    if (props.selectedTemplate) {
      setValues();
    }
  },
  { immediate: true, deep: true }
);

// Lifecycle
onMounted(() => {
  if (props.selectedTemplate) {
    setValues();
  }
});
</script>

<style scoped>
.template-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.template-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}
.template-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}
.template-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.template-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.form-body {
  padding: 16px 20px;
}

.form {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-field {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.form-field-full {
  grid-column: 1 / -1;
}

.content-label {
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.content-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.content-label-row .content-label {
  margin-bottom: 0;
}

.required-field {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

:deep(.ql-toolbar) {
  padding: 4px 8px;
  font-size: 10px;
}

:deep(.ql-toolbar .ql-formats) {
  margin-right: 6px;
}

:deep(.ql-toolbar button) {
  padding: 2px 4px;
  font-size: 10px;
  width: 20px;
  height: 20px;
}

:deep(.ql-toolbar .ql-picker) {
  font-size: 10px;
}

.template-stats-section {
  background: #fafbfc;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 6px;
}

.template-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.stat-card {
  background: #ffffff;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s ease;
}

.stat-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.stat-icon {
  color: #6b7280;
  margin-bottom: 6px;
  display: block;
  width: 14px;
  height: 14px;
}

.stat-icon.positive {
  color: #10b981;
}

.stat-icon.negative {
  color: #ef4444;
}

.stat-label {
  font-size: 10px;
  color: #6b7280;
  margin-top: 6px;
  font-weight: 500;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 2px 0;
  line-height: 1;
}

.stat-controls-inline {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin: 2px 0;
}

.stat-controls-inline button {
  width: 20px;
  height: 20px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.stat-controls-inline button:hover:not(:disabled) {
  border-color: #3e3efe;
  color: #3e3efe;
  background: #ffffff;
}

.stat-controls-inline button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.stat-controls-inline .stat-value {
  font-size: 16px;
  margin: 0;
  min-width: 24px;
  text-align: center;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
